// frontend/src/components/search/SearchBar.js
//
// Search input with gold left accent bar, language chips,
// and an elegant suggestions dropdown (trending + typeahead).

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSetting } from '../../hooks/useSettings';
import ApiService from '../../services/ApiService';
import styles from './SearchBar.module.css';

const LANGUAGES = [
  { value: 'auto',     label: 'Auto' },
  { value: 'sanskrit', label: '\u0938\u0902\u0938\u094d\u0915\u0943\u0924' },
  { value: 'telugu',   label: '\u0c24\u0c46\u0c32\u0c41\u0c17\u0c41' },
  { value: 'english',  label: 'English' },
];

const RECENT_KEY  = 'vakya_recent_searches';
const MAX_RECENT  = 8;

function getRecentSearches() {
  try {
    return JSON.parse(sessionStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function addRecentSearch(query) {
  try {
    const existing = getRecentSearches().filter(q => q !== query);
    const updated  = [query, ...existing].slice(0, MAX_RECENT);
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  } catch {}
}

export default function SearchBar({ onSearch, loading = false, initialQuery = '' }) {
  const [query,       setQuery]       = useState(initialQuery);
  const [defaultLang] = useSetting('defaultLanguage');
  const [language,    setLanguage]    = useState(defaultLang || 'auto');

  // Suggestions state
  const [open,        setOpen]        = useState(false);
  const [trending,    setTrending]    = useState([]);
  const [typeahead,   setTypeahead]   = useState([]);
  const [recent,      setRecent]      = useState([]);
  const [activeIdx,   setActiveIdx]   = useState(-1);
  const [loadingTrend, setLoadingTrend] = useState(false);

  const wrapperRef  = useRef(null);
  const inputRef    = useRef(null);
  const debounceRef = useRef(null);

  // Load trending on mount
  useEffect(() => {
    setLoadingTrend(true);
    ApiService.getSuggestions('', 20)
      .then(r => setTrending(r.trending || []))
      .catch(() => {})
      .finally(() => setLoadingTrend(false));
  }, []);

  // Debounced typeahead
  const fetchTypeahead = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (!q.trim()) { setTypeahead([]); return; }
    debounceRef.current = setTimeout(() => {
      ApiService.getSuggestions(q, 8)
        .then(r => setTypeahead(r.typeahead || []))
        .catch(() => {});
    }, 180);
  }, []);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIdx(-1);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleFocus() {
    setRecent(getRecentSearches());
    setOpen(true);
    setActiveIdx(-1);
  }

  function handleChange(e) {
    const val = e.target.value;
    setQuery(val);
    setActiveIdx(-1);
    fetchTypeahead(val);
  }

  // All suggestions in keyboard order: typeahead > recent > trending
  function allSuggestions() {
    const q = query.trim().toLowerCase();
    const seen = new Set();
    const result = [];
    const add = (item, type) => {
      if (!seen.has(item)) { seen.add(item); result.push({ text: item, type }); }
    };
    if (q) typeahead.forEach(s => add(s, 'typeahead'));
    recent.filter(s => !q || s.toLowerCase().includes(q)).forEach(s => add(s, 'recent'));
    trending.filter(s => !q || s.toLowerCase().includes(q)).slice(0, 12).forEach(s => add(s, 'trending'));
    return result.slice(0, 20);
  }

  function handleKeyDown(e) {
    const suggestions = allSuggestions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && suggestions[activeIdx]) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIdx].text);
      } else {
        handleSubmit(e);
      }
    }
  }

  function selectSuggestion(text) {
    setQuery(text);
    setOpen(false);
    setActiveIdx(-1);
    addRecentSearch(text);
    onSearch({ query: text, language });
  }

  function handleSubmit(e) {
    if (e) e.preventDefault();
    const trimmed = query.trim();
    if (trimmed && !loading) {
      addRecentSearch(trimmed);
      setOpen(false);
      onSearch({ query: trimmed, language });
    }
  }

  const suggestions = allSuggestions();
  const showDropdown = open && (
    suggestions.length > 0 || loadingTrend ||
    (trending.length > 0 && !query.trim())
  );

  // Group for display
  const hasTypeahead = suggestions.some(s => s.type === 'typeahead');
  const hasRecent    = suggestions.some(s => s.type === 'recent');
  const hasTrending  = suggestions.some(s => s.type === 'trending');

  function highlightMatch(text, q) {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className={styles.suggMark}>{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  }

  let globalIdx = 0;

  function renderGroup(label, icon, items) {
    if (!items.length) return null;
    return (
      <div className={styles.suggGroup}>
        <div className={styles.suggGroupLabel}>
          <span className={styles.suggGroupIcon}>{icon}</span>
          {label}
        </div>
        {items.map(({ text }) => {
          const idx = globalIdx++;
          return (
            <button
              key={text}
              className={`${styles.suggItem} ${activeIdx === idx ? styles.suggItemActive : ''}`}
              onMouseDown={e => { e.preventDefault(); selectSuggestion(text); }}
              onMouseEnter={() => setActiveIdx(idx)}
            >
              <span className={styles.suggText}>
                {highlightMatch(text, query)}
              </span>
              <span className={styles.suggArrow}>\u2197</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={`${styles.inputWrap} ${open ? styles.inputWrapOpen : ''}`}>
          <div className={styles.accentBar} />
          <input
            ref={inputRef}
            className={styles.input}
            type="search"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder="Search Sanskrit, Telugu, English\u2026"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Search classical verses"
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            disabled={loading}
          />
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={!query.trim() || loading}
            aria-label="Search"
          >
            {loading ? '\u2026' : '\u2192'}
          </button>
        </div>

        {/* Suggestions dropdown */}
        {showDropdown && (
          <div className={styles.suggPanel} role="listbox">
            {loadingTrend && !trending.length ? (
              <div className={styles.suggLoading}>Loading suggestions\u2026</div>
            ) : (
              <>
                {(() => { globalIdx = 0; return null; })()}
                {hasTypeahead && renderGroup('Matches', '\u2315', suggestions.filter(s => s.type === 'typeahead'))}
                {hasRecent    && renderGroup('Recent',  '\u29be', suggestions.filter(s => s.type === 'recent'))}
                {hasTrending  && renderGroup('Trending', '\u2605', suggestions.filter(s => s.type === 'trending'))}
                {!query.trim() && (
                  <div className={styles.suggFooter}>
                    Top searches from the tradition
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </form>

      <div className={styles.chips} role="group" aria-label="Language filter">
        {LANGUAGES.map(lang => (
          <button
            key={lang.value}
            className={`${styles.chip} ${language === lang.value ? styles.chipActive : ''}`}
            onClick={() => setLanguage(lang.value)}
            aria-pressed={language === lang.value}
          >
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  );
}
